/**
 * Who follows one master config and who in its group does not yet — one
 * row per container (its chip), what a follower keeps as its own on the
 * right (hover for every one), and Follow on the containers still on their
 * own settings. Honest when nobody follows.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { Link2 } from 'lucide-react';

import type { DropGenContainerSummary } from '../../../drop-generator.types';
import { ContainerChip } from '../../shared/ContainerChip';
import { OwnSettingsHint } from './OwnSettingsHint';
import { ownSettings } from './rule-facts';

interface Props {
  masterName: string;
  followers: DropGenContainerSummary[];
  candidates: DropGenContainerSummary[];
  following: string | null;
  onFollow: (containerId: string) => void;
}

const HEAD = 'flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

const Row: FC<{ c: DropGenContainerSummary; children?: ReactNode }> = ({ c, children }) => (
  <li className="flex h-7 min-w-0 items-center gap-3">
    <span className="flex min-w-0">
      <ContainerChip name={c.name} color={c.color} routeTest={c.kind === 'ROUTE_TEST'} />
    </span>
    <span className="ml-auto flex shrink-0 items-center">{children}</span>
  </li>
);

const Head: FC<{ label: string; count: number }> = ({ label, count }) => (
  <p className={HEAD}>
    <span>{label}</span>
    <span className="tabular-nums">{count}</span>
  </p>
);

const FollowButton: FC<{ busy: boolean; masterName: string; onClick: () => void }> = ({ busy, masterName, onClick }) => (
  <button
    type="button"
    disabled={busy}
    onClick={onClick}
    aria-label={`Follow ${masterName}`}
    className="inline-flex items-center gap-1 rounded-md border border-input px-1.5 py-0.5 text-[11px] font-medium text-primary hover:border-primary/50 disabled:opacity-50"
  >
    <Link2 className="h-3 w-3" /> {busy ? 'Following…' : 'Follow'}
  </button>
);

export const MasterFollowers: FC<Props> = ({ masterName, followers, candidates, following, onFollow }) => (
  <div className="flex flex-col gap-3">
    <section>
      <Head label="Following" count={followers.length} />
      {followers.length === 0 ? (
        <p className="pt-1.5 text-xs text-muted-foreground">No container follows it yet.</p>
      ) : (
        <ul className="mt-1">
          {followers.map((c) => (
            <Row key={c.id} c={c}>
              <OwnSettingsHint own={ownSettings(c)} masterName={masterName} />
            </Row>
          ))}
        </ul>
      )}
    </section>
    {candidates.length > 0 ? (
      <section>
        <Head label="Not following" count={candidates.length} />
        <ul className="mt-1">
          {candidates.map((c) => (
            <Row key={c.id} c={c}>
              <FollowButton busy={following === c.id} masterName={masterName} onClick={() => onFollow(c.id)} />
            </Row>
          ))}
        </ul>
      </section>
    ) : null}
  </div>
);
